// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ChartYAxisMetricItem.js" company="Izenda">
// Copyright (c) 2015 Izenda, Inc.  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and  International
// Copyright Laws. Unauthorized reproduction,  reverse-engineering, and
// distribution of all or any portion of  the code contained in this file is
// strictly prohibited and may  result in severe civil and criminal penalties
// and will be  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES  ARE CONFIDENTIAL AND
// PROPRIETARY TRADE  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
// LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET  CONTROLS AS
// PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED  FILES OR ANY
// PORTION OF ITS CONTENTS SHALL AT NO TIME BE  COPIED, TRANSFERRED, SOLD,
// DISTRIBUTED, OR OTHERWISE MADE  AVAILABLE TO OTHER INDIVIDUALS WITHOUT
// EXPRESS WRITTEN CONSENT  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  ADDITIONAL
// RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import DropDown from 'share/components/controls/Izenda.DropDown';
import FieldContainer from './Izenda.FieldContainer';
import Input from 'share/components/controls/Izenda.Input';
import SettingDialog from './Izenda.SettingDialog';
import EventsUtils from 'EventUtils';
import classnames from 'classnames';
import connect from 'share/enhancers/Izenda.Connect';
import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import CONSTANTS from 'logic/data/Izenda.ReportPartChartConstants';

const CHART_DEFAULT_SETTING = CONSTANTS.CHART_SETTINGS.DEFAULT_VALUE;
const OTHER_PROPS_KEY = CONSTANTS.CHART_SETTINGS.OTHER_PROPS_KEY;
const { isChartType } = CONSTANTS;

import { isEmpty, isFunction, omit } from 'lodash/fp';

@connect()
export default class ChartYAxisMetricItem extends Component {
  static propsType: { value: PropTypes.object };

  constructor(props) {
    super(props);

    this.state = { showSetting: false, element: this.props.value };
  }

  componentWillReceiveProps(nextProps) {
    this.setState({ element: nextProps.value });
  }

  render() {
    const props = this.props;
    const localization = props.localization;
    const type = props.type;
    const element = this.state.element;
    const elementProps = element && element.properties;
    const isCombinationStyle = isChartType(type, CHART_TYPE.Combination);
    const dropDownProps = isCombinationStyle
      ? CONSTANTS.COMBINE_CHART.CHART_TYPE_DROPDOWN
      : CONSTANTS.SPARK_LINE_CHART.CHART_TYPE_DROPDOWN;
    const chartType =
      (elementProps && elementProps.getOtherProps && elementProps.getOtherProps(OTHER_PROPS_KEY.chartType)) ||
      dropDownProps.value;
    const container = { elements: isEmpty(element) ? [] : [element], name: 'values' };

    let settings = {};
    if (elementProps && elementProps.getOtherProps) {
      settings = {
        dashStyle: elementProps.getOtherProps(OTHER_PROPS_KEY.dashStyle) || CHART_DEFAULT_SETTING.DASH_STYLE,
        lineWidth: elementProps.getOtherProps(OTHER_PROPS_KEY.lineWidth) || CHART_DEFAULT_SETTING.WIDTH
      };
    }

    const isColumnStyle = isChartType(chartType, CHART_TYPE.Column);
    const settingDisabled = classnames({
      'disabled-elm': isEmpty(element) || isColumnStyle
    });

    const containerProps = omit(['onDeleteElement', 'onAddElement'], props);

    return (
      <div className='yaxis-combination'>
        <div className='row'>
          <label className='col-md-4'>{localization.getText('COMMON_LABEL_VALUE')}</label>
          <div className='col-md-8'>
            <FieldContainer
              {...containerProps}
              onDeleteElement={handleDeleteElement.bind(this)}
              onAddElement={handleAddElement.bind(this)}
              container={container}
              height={40}
              showPlaceHolder={false}
              resizable={false}
              maximumField={1}
            />
          </div>
        </div>
        <div className='row'>
          <label className='col-md-4'>{localization.getText('COMMON_LABEL_CHART_TYPE')}</label>
          <div className='col-md-8'>
            <div className='metric-dropdown-container'>
              <DropDown
                {...dropDownProps}
                disabled={isEmpty(element)}
                onChange={handleChartTypeChanged.bind(this)}
                value={chartType}
              />
            </div>
          </div>
        </div>
        {this.renderShowTotal()}
        <div className='row'>
          <label className='col-md-4'>{localization.getText('REPORTDESIGNER_LABEL_SETTINGS')}</label>
          <div className='col-md-8'>
            <a className={settingDisabled} onClick={this.toggleSetting}>
              <span className='icon-setting' />
            </a>
          </div>
        </div>
        <SettingDialog
          showSetting={this.state.showSetting}
          onClosed={this.handleCloseSetting}
          title={getSettingDialogTitle(chartType, localization)}
          value={settings}
        />
      </div>
    );

    function getSettingDialogTitle(chartType, localization) {
      let chartTitle = '';

      const isLineStyle = isChartType(chartType, CHART_TYPE.Line);
      const isAreaStyle = isChartType(chartType, CHART_TYPE.Area);
      if (isLineStyle) {
        chartTitle = localization.getText('REPORTDESIGNER_LABEL_CHART_LINE');
      } else if (isAreaStyle) {
        chartTitle = localization.getText('REPORTDESIGNER_LABEL_CHART_AREA');
      }

      return [chartTitle, localization.getText('REPORTDESIGNER_LABEL_CHART_SETTINGS')].join(' ');
    }

    function handleChartTypeChanged(newVal) {
      elementProps && elementProps.setOtherProps && elementProps.setOtherProps(OTHER_PROPS_KEY.chartType, newVal.value);
      props.onUpdateElement && props.onUpdateElement(element, props.index);
      this.setState({ element });
    }

    function handleDeleteElement(deletedElement) {
      props.onDeleteElement && props.onDeleteElement(props.index, deletedElement.fieldId);
      this.setState({ element: {} });
    }

    function handleAddElement(newElement) {
      return (
        props.onAddElement &&
        props.onAddElement(newElement, props.index).then(newElement => {
          this.setState({
            element: newElement
          });
          return null;
        })
      );
    }
  }

  renderShowTotal() {
    const props = this.props;
    const localization = props.localization;
    const element = this.state.element;
    const elementProps = (element && element.properties) || {};
    const type = this.props.type;
    const isCombinationStyle = isChartType(type, CHART_TYPE.Combination);
    if (isCombinationStyle) return null;

    const otherProps = isFunction(elementProps.getOtherProps) && elementProps.getOtherProps();
    return (
      <div className='row show-total'>
        <label className='col-md-4'>{localization.getText('REPORTDESIGNER_LABEL_SHOW_TOTAL')}</label>
        <div className='col-md-8'>
          <Input
            type='checkbox'
            disabled={isEmpty(element)}
            checked={otherProps && otherProps.showTotal}
            onChange={EventsUtils.notifyChange(handleChange.bind(this), EventsUtils.CHECKBOX)}
          />
        </div>
      </div>
    );

    function handleChange(value) {
      elementProps && elementProps.setOtherProps && elementProps.setOtherProps(OTHER_PROPS_KEY.showTotal, value);
      props.onUpdateElement && props.onUpdateElement(element, props.index);
      this.setState({ element });
    }
  }

  toggleSetting = () => {
    this.setState({ showSetting: !this.state.showSetting });
  };

  handleCloseSetting = value => {
    value && this.setSettings(value);
    this.toggleSetting();
  };

  setSettings(value) {
    const props = this.props;
    const element = this.state.element;
    const elementProps = element && element.properties;
    if (elementProps && elementProps.setOtherProps) {
      elementProps.setOtherProps(OTHER_PROPS_KEY.dashStyle, value.dashStyle);
      elementProps.setOtherProps(OTHER_PROPS_KEY.lineWidth, value.lineWidth);
    }
    props.onUpdateElement && props.onUpdateElement(element, props.index);
    this.setState({ element });
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/FieldContainers/Izenda.ChartYAxisMetricItem.js